import React, {
	createContext,
	useContext,
	useEffect,
	useReducer,
	useRef,
	useState,
} from 'react';
import {QueryClientProvider} from '@tanstack/react-query';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import useDispatcher from '@netx/core-components/hooks/useDispatcher';
import {
	ModalManagerProviderProps,
	modalManagerStore,
	ModalManagerStore,
	ModalWrapperProps,
} from '@netx/core-components/ModalManager';
import {useSnackbar} from '@netx/core-components/Snackbar';
import {useReactQueryGetProgressReport} from '@netx/core-next/api';
import {queryClient} from '@netx/core-next/lib';
import {openDownloadLink} from '@netx/core-next/utils/browser';
import DownloadDialog, {DownloadOptions} from '../components/Dialog';
import setProgressReportNotify from './api/setProgressReportNotify';

const DownloadServiceContext = createContext<
	(options: DownloadOptions) => void
>(() => {});

export const useDownload = () => useContext(DownloadServiceContext);

export type DownloadServiceProviderProps = {
	disableEmailWhenDone?: boolean;
	modalManagerStore?: ModalManagerStore;
	children:
		| React.ReactNode
		| ((props: {
				openDownload: (options: DownloadOptions) => void;
		  }) => React.ReactNode);
};

export const DownloadServiceProviderPrimitive = ({
	children,
	disableEmailWhenDone,
	modalManagerStore: _modalManagerStore,
}: DownloadServiceProviderProps) => {
	const snackbar = useSnackbar();
	// TODO: this needs a lot of cleanup!
	const openDownload = useConstCallback((options: DownloadOptions) => {
		(_modalManagerStore || modalManagerStore).show(
			'DownloadDialog',
			({
				disableEmailWhenDone: _disableEmailWhenDone,
				getUUID,
				onEmailWhenDone,
				...props
			}: ModalWrapperProps & DownloadOptions) => {
				const [uuid, setUuid] = useState<string>('');
				const [error, setError] = useState<string>();
				const [pendingApproval, setPendingApproval] = useState(false);
				const [progress, setProgress] = useState<number>(0);
				const [completeUrl, setCompleteUrl] = useState<string>();

				const claimsCompleteWithoutUrl = useRef(false);

				const handleEmailWhenDone = useConstCallback(() => {
					if (uuid) {
						setProgressReportNotify({uuid, notify: true});
						onEmailWhenDone?.();
					}
					modalManagerStore.hide('DownloadDialog');
				});

				const query = useReactQueryGetProgressReport(
					{
						uuid,
					},
					{
						enabled: !!uuid,
						refetchInterval: 1000,
					},
				);

				useEffect(() => {
					if (!query.data) return;
					setProgress(query.data.percentComplete);

					if (
						query.data.percentComplete >= 100 &&
						query.data.completeUrl != null
					) {
						setCompleteUrl(query.data.completeUrl);
						openDownloadLink(query.data.completeUrl);
						modalManagerStore.hide('DownloadDialog');
					}
					// DAM-23391
					// ugh, so we generally test for `completeUrl` not being `null`
					// to avoid false 100% reports... but things like empty folders
					// throw no errors/warnings and instead report 100% complete with no URL
					else if (
						query.data.percentComplete >= 100 &&
						query.data.percentLastLogged >= 100 &&
						query.data.completeUrl == null
					) {
						if (claimsCompleteWithoutUrl.current) {
							modalManagerStore.hide('DownloadDialog');
							snackbar({
								message: i18n.t('i18n.noAssetsAvailable'),
								severity: 'warning',
							});
						}
						claimsCompleteWithoutUrl.current = true;
					}
				}, [query.data, query.dataUpdatedAt]);

				useEffect(() => {
					getUUID()
						.then(setUuid)
						.catch((error) => {
							if (error.code === 221) {
								setPendingApproval(true);
								return;
							}
							setError(error.message);
						});
				}, []);

				return (
					<DownloadDialog
						completeUrl={completeUrl}
						disableEmailWhenDone={disableEmailWhenDone}
						onEmailWhenDone={
							_disableEmailWhenDone || disableEmailWhenDone
								? undefined
								: handleEmailWhenDone
						}
						{...props}
						{...props.DialogProps}
						pendingApproval={pendingApproval}
						progress={progress}
						error={query.error?.message ?? error}
						uuid={uuid}
						{...(options as DownloadOptions)}
					/>
				);
			},
			options,
		);
	});

	useDispatcher('react:download:dialog', openDownload);

	return (
		<DownloadServiceContext.Provider value={openDownload}>
			{typeof children === 'function' ? children({openDownload}) : children}
		</DownloadServiceContext.Provider>
	);
};

export const DownloadServiceProvider = (
	props: DownloadServiceProviderProps,
) => {
	return (
		<QueryClientProvider client={queryClient}>
			<DownloadServiceProviderPrimitive {...props} />
		</QueryClientProvider>
	);
};

export default DownloadServiceProvider;
